import { forwardRef, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Share2, Loader2, Snowflake } from "lucide-react";
import { toast } from "sonner";
import { STATUS_META } from "@/lib/copy";
import { nodeToPng, shareOrDownload } from "@/lib/share";
import { bumpStreak } from "@/lib/device";
import { haptic } from "@/lib/haptics";
import { personalityFor } from "@/lib/fun";

const FORMATS = {
  story: { w: 1080, h: 1920, label: "9:16" },
  square: { w: 1080, h: 1080, label: "1:1" },
};

const STYLES = {
  editorial: { label: "Editorial" },
  frost: { label: "Frost" },
  receipt: { label: "Receipt" },
};

// ---------- EDITORIAL (paper) ----------
function Editorial({ result, isStory }) {
  const items = (result.items || []).slice(0, isStory ? 5 : 3);
  return (
    <div style={{ width: "100%", height: "100%", background: "#f4f1ea", color: "#1a1a1a", padding: isStory ? "110px 90px" : "80px", display: "flex", flexDirection: "column", boxSizing: "border-box", fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div style={{ fontSize: 30, letterSpacing: 8, textTransform: "uppercase", fontWeight: 600, color: "#5c5c5c" }}>The verdict</div>
      <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: isStory ? 124 : 92, lineHeight: 0.95, letterSpacing: -3, marginTop: 40 }}>{result.summary_stat || "Case closed."}</div>
      <div style={{ height: 2, background: "#e2dfd8", margin: isStory ? "60px 0" : "44px 0" }} />
      <div style={{ display: "flex", flexDirection: "column", gap: isStory ? 44 : 30, flex: 1 }}>
        {items.length === 0 ? (
          <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: isStory ? 56 : 44, lineHeight: 1.2 }}>{result.vibe_copy || "Something shifted in there."}</div>
        ) : items.map((it, i) => {
          const meta = STATUS_META[it.status] || STATUS_META.still_here;
          return (
            <div key={`${it.name}-${i}`} style={{ display: "flex", gap: 28, alignItems: "flex-start" }}>
              <div style={{ width: 26, height: 26, borderRadius: 999, background: meta.color, marginTop: 14, flexShrink: 0 }} />
              <div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: isStory ? 58 : 46, lineHeight: 1.05 }}>{it.name}</div>
                <div style={{ fontSize: isStory ? 34 : 28, color: "#5c5c5c", marginTop: 8, lineHeight: 1.3 }}>{it.copy}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: isStory ? 32 : 27, color: "#5c5c5c", margin: isStory ? "36px 0 30px" : "26px 0 22px" }}>
        Fridge personality — <span style={{ color: "#1a1a1a", fontWeight: 700 }}>{personalityFor(result).title}</span>
      </div>
      <Watermark dark />
    </div>
  );
}

// ---------- FROST (fridge interior) ----------
function Frost({ result, isStory }) {
  const items = (result.items || []).slice(0, isStory ? 5 : 3);
  return (
    <div style={{ width: "100%", height: "100%", color: "#16242a", padding: isStory ? "110px 90px" : "80px", display: "flex", flexDirection: "column", boxSizing: "border-box", fontFamily: "'IBM Plex Sans', sans-serif", background: "linear-gradient(180deg, #f2f8f9 0%, #dbe8ea 55%, #c6d6d9 100%)" }}>
      <div style={{ fontSize: 30, letterSpacing: 8, textTransform: "uppercase", fontWeight: 600, color: "#5a7176" }}>Inside the fridge</div>
      <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: isStory ? 124 : 92, lineHeight: 0.95, letterSpacing: -3, marginTop: 40 }}>{result.summary_stat || "Case closed."}</div>
      <div style={{ height: 4, background: "rgba(255,255,255,0.9)", boxShadow: "0 8px 16px -8px rgba(20,40,45,0.4)", margin: isStory ? "60px 0" : "44px 0" }} />
      <div style={{ display: "flex", flexDirection: "column", gap: isStory ? 34 : 24, flex: 1 }}>
        {items.length === 0 ? (
          <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: isStory ? 56 : 44, lineHeight: 1.2 }}>{result.vibe_copy || "Something shifted in there."}</div>
        ) : items.map((it, i) => {
          const meta = STATUS_META[it.status] || STATUS_META.still_here;
          return (
            <div key={`${it.name}-${i}`} style={{ display: "flex", gap: 24, alignItems: "flex-start", background: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.8)", borderRadius: 10, padding: isStory ? "26px 30px" : "20px 24px" }}>
              <div style={{ width: 24, height: 24, borderRadius: 999, background: meta.color, marginTop: 12, flexShrink: 0 }} />
              <div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: isStory ? 52 : 42, lineHeight: 1.05 }}>{it.name}</div>
                <div style={{ fontSize: isStory ? 32 : 26, color: "#5a7176", marginTop: 6, lineHeight: 1.3 }}>{it.copy}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: isStory ? 32 : 27, color: "#5a7176", margin: isStory ? "36px 0 30px" : "26px 0 22px" }}>
        Fridge personality — <span style={{ color: "#16242a", fontWeight: 700 }}>{personalityFor(result).title}</span>
      </div>
      <Watermark dark />
    </div>
  );
}

// ---------- RECEIPT (tabloid till roll) ----------
function Receipt({ result, isStory }) {
  const items = (result.items || []).slice(0, isStory ? 7 : 4);
  const dash = "- - - - - - - - - - - - - - - - - - - - - - - - -";
  const label = { gone: "GONE", new: "NEW", still_here: "STAYED" };
  return (
    <div style={{ width: "100%", height: "100%", background: "#fbfbf6", color: "#111", padding: isStory ? "110px 100px" : "80px", display: "flex", flexDirection: "column", boxSizing: "border-box", fontFamily: "'IBM Plex Mono', monospace" }}>
      <div style={{ textAlign: "center", fontSize: 40, fontWeight: 600, letterSpacing: 4 }}>FRIDGE RECEIPT</div>
      <div style={{ textAlign: "center", fontSize: 26, color: "#555", marginTop: 10 }}>* itemised losses & arrivals *</div>
      <div style={{ fontSize: 24, color: "#999", margin: "34px 0 20px", overflow: "hidden", whiteSpace: "nowrap" }}>{dash}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: isStory ? 30 : 22, flex: 1 }}>
        {items.length === 0 ? (
          <div style={{ fontSize: isStory ? 40 : 32, lineHeight: 1.3 }}>{result.vibe_copy || "NO CLEAR ACTIVITY."}</div>
        ) : items.map((it, i) => (
          <div key={`${it.name}-${i}`}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: isStory ? 42 : 34, fontWeight: 500 }}>
              <span style={{ textTransform: "uppercase" }}>{it.name}</span>
              <span>{label[it.status] || "?"}</span>
            </div>
            <div style={{ fontSize: isStory ? 28 : 24, color: "#666", marginTop: 6, lineHeight: 1.3 }}>{it.copy}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 24, color: "#999", margin: "20px 0", overflow: "hidden", whiteSpace: "nowrap" }}>{dash}</div>
      <div style={{ textAlign: "center", fontSize: 30, fontWeight: 600 }}>{result.summary_stat || "CASE CLOSED"}</div>
      <div style={{ textAlign: "center", fontSize: 26, color: "#111", marginTop: 14, textTransform: "uppercase", letterSpacing: 2 }}>PERSONALITY: {personalityFor(result).title}</div>
      <div style={{ textAlign: "center", fontSize: 26, color: "#555", marginTop: 14 }}>thank you — What's in the Fridge</div>
    </div>
  );
}

function Watermark({ dark }) {
  const c = dark ? "#16242a" : "#f4f1ea";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 44 }}>
      <div style={{ width: 34, height: 34, borderRadius: 999, border: `3px solid ${c}` }} />
      <div style={{ fontSize: 30, fontWeight: 600, letterSpacing: 1, color: c }}>What's in the Fridge</div>
    </div>
  );
}

const RENDERERS = { editorial: Editorial, frost: Frost, receipt: Receipt };

const ShareCard = forwardRef(({ result, fmt, style }, ref) => {
  const isStory = fmt.h > 1200;
  const Renderer = RENDERERS[style] || Editorial;
  return (
    <div ref={ref} style={{ width: fmt.w, height: fmt.h }}>
      <Renderer result={result} isStory={isStory} />
    </div>
  );
});
ShareCard.displayName = "ShareCard";

export default function ShareCardModal({ result, onClose }) {
  const [format, setFormat] = useState("story");
  const [style, setStyle] = useState("frost");
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  const fmt = FORMATS[format];
  const previewW = 300;
  const scale = previewW / fmt.w;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleShare = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await nodeToPng(cardRef.current);
      await shareOrDownload(dataUrl, `fridge-${style}-${format}.png`, "What's in the Fridge — the verdict.");
      const n = bumpStreak();
      haptic([12, 40, 12]);
      toast(`That's ${n} card${n > 1 ? "s" : ""} shared. The loop tightens.`);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="share-modal">
      <motion.div className="w-full max-w-md bg-[#eef5f6]/95 backdrop-blur rounded-t-2xl sm:rounded-lg p-6 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] max-h-[94dvh] overflow-y-auto" initial={{ y: 40 }} animate={{ y: 0 }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-2xl">Your share card</h3>
          <button data-testid="share-close-btn" onClick={onClose} className="w-9 h-9 rounded-full glass flex items-center justify-center">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex justify-center">
          <div style={{ width: previewW, height: fmt.h * scale }} className="overflow-hidden rounded-sm hairline shadow-[0_10px_30px_rgba(20,40,45,0.18)]">
            <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
              <ShareCard ref={cardRef} result={result} fmt={fmt} style={style} />
            </div>
          </div>
        </div>

        {/* Style */}
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4a5c62] mt-6 mb-2">Style</p>
        <div className="flex gap-2">
          {Object.entries(STYLES).map(([key, s]) => (
            <button key={key} data-testid={`style-${key}-btn`} onClick={() => setStyle(key)} className={`flex-1 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${style === key ? "bg-[#16242a] text-[#eef5f6]" : "glass text-[#4a5c62]"}`}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Format */}
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4a5c62] mt-4 mb-2">Format</p>
        <div className="flex gap-2">
          {Object.entries(FORMATS).map(([key, f]) => (
            <button key={key} data-testid={`format-${key}-btn`} onClick={() => setFormat(key)} className={`flex-1 px-4 py-2.5 rounded-sm text-sm font-medium transition-colors ${format === key ? "bg-[#16242a] text-[#eef5f6]" : "glass text-[#4a5c62]"}`}>
              {f.label}
            </button>
          ))}
        </div>

        <button data-testid="share-generate-btn" onClick={handleShare} disabled={busy} className="btn-ink w-full rounded-sm py-4 mt-5 flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-5 h-5 spin-slow" /> : <Share2 className="w-5 h-5" strokeWidth={1.5} />}
          {busy ? "Developing…" : "Share the verdict"}
        </button>
      </motion.div>
    </motion.div>
  );
}
